import React, { FC, memo, useContext, useEffect, useMemo } from "react";

// types
import { LogoCtx, CroutonComms, CroutonPriority, CroutonIds, GenericCroutonProps } from "@swiggy-private/cart-types";

// Components
import { useEffectOnce } from "@swiggy-private/core-ui";
import CroutonText from "./CroutonText";
import CloseIcon from "../CloseIcon/CloseIcon";

// DLS
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// utils
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import { CROUTON_LOGO_DIMENTIONS } from "@super-ui/components/black-membership-widgets/constants";
import { closeIconStyles, CroutonLogo, croutonStyles } from "./styles";

// constants
import { CROUTON_DURATION } from "@super-ui/components/black-membership-widgets/constants";
import { DATA_TEST_IDS } from "@swiggy-private/cart-test-ids";

// deps
import { CartOverlaysContext } from "@swiggy-private/checkout-core";

interface BlackPlanCroutonProps extends CroutonComms {
    analyticsEvents: {
        onImpressionCrouton: () => void;
        onClickCroutonClose: () => void;
    };
}

interface BlackPlanCroutonProps extends CroutonComms {
    analyticsEvents: {
        onImpressionCrouton: () => void;
        onClickCroutonClose: () => void;
    };
}

const BlackLogoComponent: FC<{ logoCtx: LogoCtx }> = ({ logoCtx }) => {
    return (
        <CroutonLogo data-testid={DATA_TEST_IDS.CROUTON_LOGO}>
            <Image cloudinaryId={logoCtx.logo} alt={logoCtx.text} {...CROUTON_LOGO_DIMENTIONS} />
        </CroutonLogo>
    );
};

/* eslint-disable @typescript-eslint/naming-convention */
const BlackPlanCrouton: FC<BlackPlanCroutonProps> = ({ logo_ctx: logoCtx, subtitles, analyticsEvents }) => {
    const { onImpressionCrouton, onClickCroutonClose } = analyticsEvents;

    useEffectOnce(() => {
        onImpressionCrouton();
    });

    const leftIcon = useMemo(() => {
        if (isEmpty(logoCtx)) {
            return undefined;
        }
        return { icon: () => <BlackLogoComponent logoCtx={logoCtx} /> };
    }, [logoCtx]);

    const titleNode = useMemo(
        () => subtitles.map((subTitle: string) => <CroutonText key={subTitle} text={subTitle} />),
        [subtitles],
    );

    const isOnlyOneLine = useMemo(() => subtitles.length === 1, [subtitles]);

    const croutonContent: GenericCroutonProps = useMemo(
        () => ({
            cssProperties: croutonStyles,
            title: titleNode,
            leftIcon,
            closeIcon: (
                <CloseIcon styles={closeIconStyles(isOnlyOneLine)} dataTestId={DATA_TEST_IDS.CROUTON_CLOSE_BUTTON} />
            ),
            durationOfCrouton: CROUTON_DURATION,
            onClose: onClickCroutonClose,
        }),
        [isOnlyOneLine, leftIcon, onClickCroutonClose, titleNode],
    );

    const { registerCrouton } = useContext(CartOverlaysContext);

    useEffect(() => {
        if (!isEmpty(subtitles)) {
            registerCrouton({
                id: CroutonIds.ONE_BLACK_OTP,
                priority: CroutonPriority.PRIORITY_ORDER_TWO,
                content: croutonContent,
            });
        }
    }, [croutonContent, registerCrouton, subtitles]);

    return null;
};

export default memo(BlackPlanCrouton);
